/**
 * Logística → Notas de Venta (`/logistica/notas-venta`).
 *
 * Puerto de `Sales::receipt` + `views/sales/receipt.php` + el *bundle*
 * `sales.notes.bundle.js`: el listado de notas de venta con sus filtros (fechas
 * del mes en curso, cliente y monto mínimo), las mismas columnas, el **PDF de la
 * nota** (que genera el propio panel) y el envío por WhatsApp con un enlace firmado
 * que el cliente puede abrir sin sesión.
 *
 * Igual que en Documentos, las acciones que dependen de otros sistemas se
 * muestran con un candado y su motivo:
 * - *Editar método de pago*: mueve el efectivo del turno, se habilita con
 *   **Bóvedas**.
 * - *Eliminar la nota*: además de borrarla, el panel anterior anulaba la factura
 *   en el **controlador PTS** y devolvía los productos al **stock**, así que se
 *   portará cuando se revise ese circuito.
 */

import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  FileCode,
  FileText,
  Filter,
  Lock,
  MessageCircle,
  RotateCcw,
  ShieldAlert,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { CampoFecha } from '@/components/ui/calendario';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado, urlDeApi } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import { useEnlaceNotaVenta, useNotasVenta } from '../notas-venta.api';
import type { NotaVenta, NotasVentaFiltros } from '../notas-venta.types';

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

const POR_PAGINA = 10;

/** Fecha local en `AAAA-MM-DD` (no usar `toISOString`, que cambia de día). */
function fechaLocal(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** Rango por defecto: el mes en curso, como los campos del panel anterior. */
function rangoPorDefecto(): { desde: string; hasta: string } {
  const hoy = new Date();

  return {
    desde: fechaLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
    hasta: fechaLocal(hoy),
  };
}

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** Botón de descarga, de PDF propio o de acción pendiente. */
function Accion({
  titulo,
  href,
  pendiente,
  cargando,
  onClick,
  icono: Icono,
}: {
  titulo: string;
  href?: string | null;
  pendiente?: string;
  cargando?: boolean;
  onClick?: () => void;
  icono: typeof Download;
}) {
  if (pendiente) {
    return (
      <Button variant="ghost" size="icon" title={`${titulo} · ${pendiente}`} disabled>
        <Lock className="size-4 text-ink-300" aria-hidden="true" />
      </Button>
    );
  }

  if (onClick) {
    return (
      <Button
        variant="ghost"
        size="icon"
        title={titulo}
        aria-label={titulo}
        loading={cargando}
        onClick={onClick}
      >
        <Icono className="size-4 text-ink-600" aria-hidden="true" />
      </Button>
    );
  }

  if (!href) {
    return (
      <Button variant="ghost" size="icon" title={`${titulo} · no disponible`} disabled>
        <Icono className="size-4 text-ink-300" aria-hidden="true" />
      </Button>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      title={titulo}
      className="inline-flex size-10 items-center justify-center rounded-control text-ink-600 transition-colors hover:bg-ink-100 hover:text-ink-900"
    >
      <Icono className="size-4" aria-hidden="true" />
    </a>
  );
}

/** Diálogo para enviar el enlace del comprobante por WhatsApp. */
function WhatsAppDialogo({ nota, onCerrar }: { nota: NotaVenta; onCerrar: () => void }) {
  const [telefono, setTelefono] = useState('');
  const consulta = useEnlaceNotaVenta(nota.id);
  const enlace = consulta.data ? urlDeApi(consulta.data.ruta) : null;
  const valido = telefono.replace(/\D/g, '').length >= 9;

  return (
    <Dialog
      open
      onClose={onCerrar}
      title="Enviar la nota de venta por WhatsApp"
      description={`${nota.documento} · ${nota.cliente || 'cliente no informado'}`}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            size="sm"
            disabled={!valido || !enlace}
            leftIcon={<MessageCircle className="size-3.5" aria-hidden="true" />}
            onClick={() => {
              if (!enlace) return;

              const mensaje =
                'Su comprobante de pago electrónico ha sido generado correctamente, puede revisarlo en el siguiente enlace: ';

              window.open(
                `https://wa.me/+51${telefono.replace(/\D/g, '')}/?text=${encodeURIComponent(mensaje + enlace)}`,
                '_blank',
              );
              onCerrar();
            }}
          >
            Abrir WhatsApp
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {enlace ? (
          <Alert variant="info">
            Se enviará el enlace a la nota de venta:{' '}
            <a href={enlace} target="_blank" rel="noreferrer" className="font-semibold underline">
              ver la nota
            </a>
          </Alert>
        ) : consulta.isLoading ? (
          <Alert variant="info">Preparando el enlace de la nota…</Alert>
        ) : (
          <Alert variant="warning">
            No se pudo preparar el enlace de la nota de venta.
          </Alert>
        )}

        <Input
          label="Número de WhatsApp"
          value={telefono}
          autoFocus
          inputMode="numeric"
          maxLength={15}
          placeholder="9 dígitos, sin +51"
          hint="Se envía con el prefijo +51"
          onChange={(evento) => setTelefono(evento.target.value)}
        />
      </div>
    </Dialog>
  );
}

export function NotasVentaPage() {
  useDocumentTitle('Logística · Notas de Venta');

  const inicial = rangoPorDefecto();
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const [cliente, setCliente] = useState('');
  const [montoMin, setMontoMin] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtros, setFiltros] = useState<NotasVentaFiltros>({
    desde: inicial.desde,
    hasta: inicial.hasta,
    perPage: POR_PAGINA,
    page: 1,
  });
  const [aEnviar, setAEnviar] = useState<NotaVenta | null>(null);
  const [imprimiendo, setImprimiendo] = useState<number | null>(null);
  const [errorImpresion, setErrorImpresion] = useState<string | null>(null);

  const notas = useNotasVenta(filtros);
  const sinPermiso = notas.error instanceof ApiError && notas.error.status === 403;
  const lista = notas.data?.notas ?? [];
  const total = notas.data?.total ?? 0;
  const pagina = notas.data?.page ?? 1;
  const paginas = notas.data?.paginas ?? 1;
  const desdeFila = total === 0 ? 0 : (pagina - 1) * POR_PAGINA + 1;
  const hastaFila = Math.min(pagina * POR_PAGINA, total);

  /** Aplica los filtros y vuelve a la primera página. */
  function aplicar() {
    setFiltros({
      desde: desde || undefined,
      hasta: hasta || undefined,
      cliente: cliente.trim() || undefined,
      montoMin: montoMin.trim() || undefined,
      search: busqueda.trim() || undefined,
      perPage: POR_PAGINA,
      page: 1,
    });
  }

  /** Abre la nota de venta (la genera la propia API). */
  async function imprimirNota(nota: NotaVenta) {
    setErrorImpresion(null);
    setImprimiendo(nota.id);

    try {
      await abrirPdfAutenticado(`/api/logistica/notas-venta/${nota.id}/ticket`);
    } catch (error) {
      setErrorImpresion(mensajeDe(error, 'No se pudo generar la nota de venta.'));
    } finally {
      setImprimiendo(null);
    }
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/logistica"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Logística
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <MessageCircle className="size-6 text-brand-500" aria-hidden="true" />
          Notas de Venta
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Comprobantes internos emitidos en cada venta, con el turno en el que se hicieron y el envío
          del comprobante por WhatsApp.
        </p>
      </div>

      {notas.data ? <Badge variant="outline">{formatNumber(total)} notas</Badge> : null}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>logistics</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      <Alert variant="info" title="Acciones en curso de migración">
        El envío por <strong>WhatsApp</strong> (con el enlace firmado de la nota), el{' '}
        <strong>ticket de 80 mm</strong> y las descargas de <strong>XML y CDR</strong> ya están
        disponibles. La <strong>edición del método de pago</strong> mueve el efectivo del turno (se
        habilita con <strong>Bóvedas</strong>) y <strong>eliminar la nota</strong> anula además la
        factura en el controlador PTS y devuelve los productos al stock, así que se portará junto con
        ese circuito.
      </Alert>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="nv-desde" className="text-sm font-medium text-ink-700">
                Fecha inicio
              </label>
              <CampoFecha
                id="nv-desde"
               
                className={cn(CONTROL, 'w-40')}
                value={desde}
                onChange={(evento) => setDesde(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="nv-hasta" className="text-sm font-medium text-ink-700">
                Fecha fin
              </label>
              <CampoFecha
                id="nv-hasta"
               
                className={cn(CONTROL, 'w-40')}
                value={hasta}
                onChange={(evento) => setHasta(evento.target.value)}
               tipo="date" />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="nv-cliente" className="text-sm font-medium text-ink-700">
                Cliente
              </label>
              <input
                id="nv-cliente"
                className={cn(CONTROL, 'w-56')}
                placeholder="Nombre del cliente"
                value={cliente}
                onChange={(evento) => setCliente(evento.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="nv-monto" className="text-sm font-medium text-ink-700">
                Monto mínimo
              </label>
              <input
                id="nv-monto"
                type="number"
                step="0.01"
                min={0}
                className={cn(CONTROL, 'w-32')}
                placeholder="0.00"
                value={montoMin}
                onChange={(evento) => setMontoMin(evento.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="nv-buscar" className="text-sm font-medium text-ink-700">
                Búsqueda libre
              </label>
              <input
                id="nv-buscar"
                className={cn(CONTROL, 'w-48')}
                placeholder="Serie, número, DNI…"
                value={busqueda}
                onChange={(evento) => setBusqueda(evento.target.value)}
              />
            </div>

            <Button
              size="sm"
              leftIcon={<Filter className="size-3.5" aria-hidden="true" />}
              onClick={aplicar}
            >
              Filtrar
            </Button>

            <Button
              variant="outline"
              size="sm"
              leftIcon={<RotateCcw className="size-3.5" aria-hidden="true" />}
              onClick={() => {
                const nuevo = rangoPorDefecto();

                setDesde(nuevo.desde);
                setHasta(nuevo.hasta);
                setCliente('');
                setMontoMin('');
                setBusqueda('');
                setFiltros({ desde: nuevo.desde, hasta: nuevo.hasta, perPage: POR_PAGINA, page: 1 });
              }}
            >
              Mes en curso
            </Button>
          </div>

          {errorImpresion ? (
            <Alert variant="error" title="No se pudo generar la nota de venta">
              {errorImpresion}
            </Alert>
          ) : null}

          {notas.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : notas.error ? (
            <Alert variant="error" title="No se pudieron leer las notas de venta">
              {mensajeDe(notas.error, 'La API no respondió.')}
            </Alert>
          ) : lista.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              No hay notas de venta con esos filtros.
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">Fecha</th>
                    <th className="py-2">Cliente</th>
                    <th className="py-2">Placa</th>
                    <th className="py-2">Serie</th>
                    <th className="py-2">Número</th>
                    <th className="py-2">Grifero</th>
                    <th className="py-2 text-right">Monto</th>
                    <th className="py-2 text-right">Opciones</th>
                    <th className="py-2">Turno</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map((nota) => (
                    <tr key={nota.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-3 text-ink-700">
                        <span className="block">{nota.fecha ?? '—'}</span>
                        <span className="block text-[11px] text-ink-400">{nota.creado ?? ''}</span>
                      </td>
                      <td className="py-3">
                        <span className="block font-semibold text-ink-900">
                          {nota.cliente || '—'}
                        </span>
                        <span className="block text-[11px] text-ink-400">DNI: {nota.dni || '—'}</span>
                      </td>
                      <td className="py-3">
                        {nota.placa ? (
                          <Badge variant="neutral">{nota.placa}</Badge>
                        ) : (
                          <span className="text-ink-300">—</span>
                        )}
                      </td>
                      <td className="py-3 font-semibold text-brand-600">{nota.serie || '—'}</td>
                      <td className="py-3 font-semibold text-ink-900">{nota.numero ?? '—'}</td>
                      <td className="py-3 text-ink-600">
                        {nota.grifero || <span className="text-ink-300">—</span>}
                      </td>
                      <td className="py-3 text-right font-semibold text-ink-900">
                        {formatCurrency(nota.monto)}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-0.5">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Enviar por WhatsApp"
                            aria-label={`Enviar la nota ${nota.documento} por WhatsApp`}
                            onClick={() => setAEnviar(nota)}
                          >
                            <MessageCircle className="size-4 text-emerald-600" aria-hidden="true" />
                          </Button>
                          <Accion
                            titulo="Ver la nota en ticket (80 mm)"
                            cargando={imprimiendo === nota.id}
                            onClick={() => void imprimirNota(nota)}
                            icono={FileText}
                          />
                          <Accion
                            titulo="Descargar XML"
                            href={nota.xml_link || null}
                            icono={FileCode}
                          />
                          <Accion
                            titulo="Descargar CDR"
                            href={nota.cdr_link || null}
                            icono={Download}
                          />
                          <Accion
                            titulo="Editar método de pago"
                            pendiente="se habilita con Bóvedas"
                            icono={Download}
                          />
                          <Accion
                            titulo="Eliminar la nota"
                            pendiente="anula en PTS y devuelve stock"
                            icono={Download}
                          />
                        </div>
                      </td>
                      <td className="py-3">
                        <span className="block text-xs text-ink-500">
                          {nota.turno_id ? `Turno #${nota.turno_id}` : '—'}
                        </span>
                        <span className="mt-1 block">
                          <Badge variant={nota.estado_verificacion === 1 ? 'success' : 'neutral'}>
                            {nota.estado_verificacion === 1 ? 'Verificado' : 'Sin verificar'}
                          </Badge>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-xs text-ink-400">
                Mostrando {formatNumber(desdeFila)}–{formatNumber(hastaFila)} de {formatNumber(total)}{' '}
                notas · página {pagina} de {formatNumber(paginas)}
              </p>

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ChevronLeft className="size-3.5" aria-hidden="true" />}
                  disabled={pagina <= 1}
                  onClick={() => setFiltros({ ...filtros, page: Math.max(1, pagina - 1) })}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  rightIcon={<ChevronRight className="size-3.5" aria-hidden="true" />}
                  disabled={pagina >= paginas}
                  onClick={() => setFiltros({ ...filtros, page: pagina + 1 })}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {aEnviar ? <WhatsAppDialogo nota={aEnviar} onCerrar={() => setAEnviar(null)} /> : null}
    </div>
  );
}
